import { useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';

import { assignVariant } from './experimentAssignment';
import { fetchActiveExperiments } from './fetchExperiments';
import type { ExperimentConfig } from './types';

const APP_NAME = 'plinkromatic';

export type UseExperimentResult = {
    /** Resolved variant name, or `undefined` while loading or when no experiment matches. */
    variant: string | undefined;
    /** True until the active-experiments fetch resolves (success or failure). */
    isLoading: boolean;
    /** Resolved experiment config (after fetch). Undefined when not found or loading. */
    experiment: ExperimentConfig | undefined;
};

/**
 * Picks an A/B variant for the given experiment name + surface.
 *
 * Lifecycle:
 *  1. On mount, fetches active experiments from `NEXT_PUBLIC_AUTH_API_URL`.
 *  2. Finds the matching experiment by `experimentName` and (optionally)
 *     `surface`. If no match, sets `variant = undefined` (treat as control).
 *  3. Calls `assignVariant` to derive a sticky bucket from the GA4 client id
 *     (or anon id fallback), persists in localStorage.
 *  4. Fires an `ab_exposure` event into the GTM dataLayer once per assignment.
 *
 * Returns `{ variant, isLoading, experiment }`.
 */
export function useExperiment(experimentName: string, surface: string): UseExperimentResult {
    const [variant, setVariant] = useState<string | undefined>(undefined);
    const [experiment, setExperiment] = useState<ExperimentConfig | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const exposureFiredRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        const apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL || process.env.NEXT_PUBLIC_AUTH_API_URL;

        if (!apiUrl) {
            setIsLoading(false);
            return;
        }

        (async () => {
            const experiments = await fetchActiveExperiments(apiUrl);

            if (cancelled) {
                return;
            }

            const match = experiments?.find(
                exp => exp.name === experimentName && exp.active && exp.surfaces.includes(surface)
            );

            if (!match) {
                setIsLoading(false);
                return;
            }

            const assigned = assignVariant(match);
            setExperiment(match);
            setVariant(assigned);
            setIsLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [experimentName, surface]);

    useEffect(() => {
        if (!variant || !experiment || exposureFiredRef.current) {
            return;
        }

        exposureFiredRef.current = true;

        try {
            const dataLayerName = TagManager.dataLayer.name;
            const dataLayer = (window as unknown as Record<string, unknown[]>)[dataLayerName];
            if (Array.isArray(dataLayer)) {
                dataLayer.push({
                    event: 'ab_exposure',
                    experiment_name: experiment.name,
                    experiment_variant: variant,
                    surface,
                    app_name: APP_NAME,
                });
            }
        } catch {
            // Best-effort: GTM may not be initialized.
        }
    }, [variant, experiment, surface]);

    return { variant, isLoading, experiment };
}
